"use client";

import { useMemo } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { pageRelatedLink } from "@/lib/functions";
import { routerHandler } from "@/lib/routes/router-links-handler";
import { RelatedProductsCarousel } from "@/components/Organisms/Product/ProductRange/RelatedProductsCarousel";
import { LimitedEditionRelatedCard } from "./LimitedEditionRelatedCard";

type RelatedHeroImage = {
	url?: string;
	responsiveImage?: unknown;
};

type RelatedEdition = {
	slug?: string;
	hero?: {
		__typename?: string;
		title?: string;
		featuredImage?: RelatedHeroImage;
		backgroundImage?: RelatedHeroImage;
		image?: RelatedHeroImage;
	};
};

type LimitedEditionRelatedProps = {
	editions?: RelatedEdition[];
	textColor?: { hex?: string; cssRgb?: string };
	buttonConfig?: any;
};

function buildEditionHref(slug: string | undefined, locale: string | undefined) {
	if (!slug) return "";

	return routerHandler({
		typename: "BrandsPageRecord",
		slug: pageRelatedLink(
			{ __typename: "BrandsPageRecord", slug },
			locale ?? ""
		),
		locale,
	});
}

function resolveHeroImage(hero: RelatedEdition["hero"]) {
	if (!hero) return undefined;
	if (hero.__typename === "SectionHeroFeaturedRecord") {
		return hero.featuredImage ?? hero.backgroundImage;
	}
	return hero.image;
}

function mapEditionToSlide(
	edition: RelatedEdition,
	locale: string | undefined,
	brandName?: string
) {
	const image = resolveHeroImage(edition?.hero);
	if (!image?.responsiveImage && !image?.url) return null;

	const href = buildEditionHref(edition.slug, locale);
	if (!href) return null;

	return {
		id: edition.slug,
		brandName,
		productName: edition.hero?.title,
		factSheetImage: image,
		href,
		hideCallToAction: false,
	};
}

export function LimitedEditionRelated({
	editions = [],
	textColor,
	buttonConfig,
}: LimitedEditionRelatedProps) {
	const { t } = useTranslation();
	const { locale } = useRouter();

	const brandName = process.env.NEXT_PUBLIC_WEBSITE_NAME;

	const slides = useMemo(
		() =>
			editions
				.map((edition) => mapEditionToSlide(edition, locale, brandName))
				.filter(Boolean) as any[],
		[editions, locale, brandName]
	);

	const carouselData = useMemo(
		() => ({
			relatedTextColor: { color: textColor },
			addRelatedTitleUnderline: false,
		}),
		[textColor]
	);

	const navStyle = {
		"--color": textColor?.hex,
		"--relatedcolor": textColor?.hex,
		"--iconColor": textColor?.hex,
		"--slide-card-bg": "transparent",
	} as React.CSSProperties;

	if (!slides.length) return null;

	return (
		<div className="container mx-auto w-full">
			<header className="mx-auto mb-10 flex max-w-3xl flex-col items-center gap-3 text-center">
				<MultiPurposeParagraph
					type="showcaseLabel"
					align="center"
					data={t("relatedEditionsLabel")}
					color={textColor}
				/>
				<MultiPurposeTitle
					tag="h2"
					type="featured"
					data={t("relatedEditionsTitle")}
					color={textColor}
					layout="center"
				/>
			</header>

			{slides.length === 1 ? (
				<div className="mx-auto my-8 flex max-w-[96rem] justify-center px-0 md:px-8">
					<LimitedEditionRelatedCard
						entry={slides[0]}
						textColor={textColor}
						buttonConfig={buttonConfig}
					/>
				</div>
			) : (
				<div className="mx-auto my-8 w-full max-w-[96rem] px-0 md:px-8">
					<RelatedProductsCarousel
						slides={slides}
						loop={slides.length > 1}
						showNav={slides.length > 1}
						data={carouselData}
						navColor={textColor}
						navStyle={navStyle}
						locale={locale}
						portfolioSlug=""
						cardVariant="limitedEdition"
						carouselLayout="gallery"
						showcaseTextColor={textColor}
						limitedEditionButtonConfig={buttonConfig}
					/>
				</div>
			)}
		</div>
	);
}
